/**
 * SWISS School Portal - Cognito OAuth callback
 * =============================================
 * Reached after Cognito Hosted UI completes Google sign-in.
 * Exchanges the auth code for tokens via AuthContext, then
 * redirects into the portal.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { recordAuthLogFailure } from '../services/authLog';
import './CognitoCallback.css';

const CognitoCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { completeCognitoSignIn } = useAuth();

  const [status, setStatus] = useState('processing'); // processing | error
  const [errorMessage, setErrorMessage] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');
    const oauthErrorDesc = params.get('error_description');

    if (oauthError) {
      setStatus('error');
      setErrorMessage(oauthErrorDesc || oauthError);
      // No identifier — Cognito/Google rejected the attempt before an
      // authorization code (and thus an id_token) ever existed.
      recordAuthLogFailure({ identifier: null, reason: oauthErrorDesc || oauthError, path: '/auth/callback' });
      return;
    }

    if (!code) {
      setStatus('error');
      setErrorMessage('Missing authorization code in callback.');
      recordAuthLogFailure({ identifier: null, reason: 'Missing authorization code in callback', path: '/auth/callback' });
      return;
    }

    completeCognitoSignIn({ code, state }).then((result) => {
      if (result.success) {
        let dest;
        if (['superadmin', 'mss_admin'].includes(result.role)) dest = '/admin';
        else if (result.role === 'system_admin' && result.systemId) dest = `/admin/systems/${result.systemId}`;
        else dest = '/dashboard';
        navigate(dest, { replace: true });
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Sign-in failed.');
      }
    });
  }, [params, completeCognitoSignIn, navigate]);

  return (
    <div className="cognito-callback">
      <div className="callback-card">
        <img
          src={`${process.env.PUBLIC_URL}/SWISS_Logo_4.webp`}
          alt="SWISS"
          className="callback-logo-img"
        />

        {status === 'processing' && (
          <>
            <Loader size={32} className="spin callback-spinner" />
            <h2>Signing you in…</h2>
            <p>Completing secure sign-in via Google and Cognito.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={32} className="callback-error-icon" />
            <h2>Sign-in failed</h2>
            <p className="callback-error-msg">{errorMessage}</p>
            <Link to="/login" className="btn btn-swiss btn-lg">
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CognitoCallback;
